import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import { getOptimizedImageUrl } from '../lib/image'
import { getCmsPage } from '../lib/cms'

const DEFAULT_ABOUT_IMAGE = 'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1900.jpeg'

export default function AboutPage() {
  const [pageData, setPageData] = useState(null)

  useEffect(() => {
    async function loadPageData() {
      try {
        const data = await getCmsPage('om-os')
        if (data) setPageData(data)
      } catch (err) {
        console.error('Error loading AboutPage CMS data:', err)
      }
    }
    loadPageData()
  }, [])

  const heroTitle = pageData?.hero_title || 'Om Lyn Entreprenør ApS'
  const heroDescription = pageData?.hero_description || 'Lyn Entreprenør Aps er en professionel virksomhed på Fyn, der specialiserer sig i jordarbejde og levering af maskiner og udstyr til byggeri. Vi tilbyder effektive og pålidelige løsninger til vores kunder.'
  const sectionBadge = pageData?.section_badge || 'Pålidelig partner'
  const sectionTitle = pageData?.section_title || 'Kvalitetsbevidste løsninger til byggeprojekter'
  const sectionImage = pageData?.section_image?.[0] || DEFAULT_ABOUT_IMAGE
  return (
    <>
      <Seo
        title="Om os - Lyn Entreprenør ApS"
        description="Erfarne entreprenører på Fyn. Lyn Entreprenør Aps er en professionel virksomhed på Fyn, der specialiserer sig i jordarbejde og levering af maskiner og udstyr til byggeri."
        ogImage="https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/fulllogo-nobuffer-5_1e74d9a2.png"
        canonicalUrl="https://www.lyn-ent.dk/om-os"
      />

      {/* Header */}
      <section className="bg-red-600 py-16 sm:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-left">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            {heroTitle}
          </h1>

          <p className="mt-4 text-lg sm:text-xl text-red-100 max-w-3xl leading-relaxed">
            {heroDescription}
          </p>
        </div>

        {/* V-shaped divider at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-8 sm:h-12 md:h-16 lg:h-20 text-white"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
          </svg>
        </div>
      </section>

      {/* Main About Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="text-xs font-bold uppercase tracking-wider text-red-600">
                {sectionBadge}
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                {sectionTitle}
              </h2>
              {pageData?.section_text ? (
                <div
                  className="space-y-4 text-base sm:text-lg text-slate-600 leading-relaxed [&>p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: pageData.section_text }}
                />
              ) : (
                <>
                  <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                    Velkommen til Lyn Entreprenør Aps - din pålidelige partner inden for jordarbejde på Fyn. Vi leverer effektive og kvalitetsbevidste løsninger til byggeprojekter, der sikrer vores kunders succes.
                  </p>
                  <p className="text-base text-slate-600 leading-relaxed">
                    Vi råder over moderne gravemaskiner, minigravere, dumpere og specialudstyr, som sikrer en hurtig og præcis udførelse af alle former for jordarbejde, udgravninger, planering, dræn og funderingsarbejde.
                  </p>
                </>
              )}

              {/* Core strengths */}
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-2">
                  <div className="flex items-center gap-2.5 mb-1 font-bold text-slate-900 text-sm">
                    <i className="fa-solid fa-clock text-red-600" aria-hidden="true" />
                    <span>Hurtig levering</span>
                  </div>
                  <p className="text-xs text-slate-600">
                    Vi rykker hurtigt ud på Fyn og sikrer at tidsplanerne overholdes.
                  </p>
                </div>

                <div className="p-2">
                  <div className="flex items-center gap-2.5 mb-1 font-bold text-slate-900 text-sm">
                    <i className="fa-solid fa-truck-monster text-red-600" aria-hidden="true" />
                    <span>Moderne maskinpark</span>
                  </div>
                  <p className="text-xs text-slate-600">
                    Maskiner og udstyr i topform for maksimal effektivitet og præcision.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-6 py-3.5 text-sm font-bold text-white hover:bg-red-700 transition"
                >
                  <span>Kontakt os for et tilbud</span>
                </Link>
              </div>
            </div>

            {/* Images */}
            <div className="lg:col-span-5 flex flex-col">
              <div className="relative rounded-t-2xl overflow-hidden aspect-[3/4] sm:aspect-[4/5] lg:aspect-auto lg:h-full w-full">
                <img
                  src={getOptimizedImageUrl(
                    sectionImage,
                    { width: 900, height: 1200, quality: 85 }
                  )}
                  alt="Lyn Entreprenør gravearbejde"
                  className="w-full h-full object-cover"
                />
                {/* V-shaped divider at the bottom of the image */}
                <div className="absolute -bottom-0.5 -left-px -right-px w-[calc(100%+2px)] overflow-hidden leading-none z-10 pointer-events-none">
                  <svg
                    className="relative block w-full h-6 sm:h-8 md:h-10 text-white"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
